import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, StyleSheet, Switch, TextInput, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Text } from '../components/Texte';
import { Button } from '../components/Button';
import { ScreenHeader } from '../components/ScreenHeader';
import { useGabarit } from '../hooks/useGabarit';
import { localeFormats, t } from '../i18n';
import {
  CodeEcoleError,
  accepterCodeEcole,
  lireSituationEcole,
  definirConsentementEmployeur,
  type EmployeurApprenti,
  type LicenceEcole,
} from '../services/licenceEcole';
import { colors, radius, screenPadding, spacing, typography } from '../theme/tokens';

// Un code d'invitation compte 12 caractères utiles : tirets et espaces ne comptent pas.
const caracteresUtiles = (code: string): number => code.replace(/[^a-zA-Z0-9]/g, '').length;

// Le serveur envoie une date (AAAA-MM-JJ) ; si elle est illisible on l'affiche telle quelle plutôt que « Invalid Date ».
const dateContrat = (iso: string): string => {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(localeFormats(), { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
};

type Phase = 'chargement' | 'erreur' | 'pret';

export function LicenceEcoleScreen({ onBack }: { onBack?: () => void }) {
  const { large } = useGabarit();
  const [phase, setPhase] = useState<Phase>('chargement');
  const [licence, setLicence] = useState<LicenceEcole | null>(null);
  const [employeur, setEmployeur] = useState<EmployeurApprenti | null>(null);
  const [employeurEnCours, setEmployeurEnCours] = useState(false);
  const [erreurEmployeur, setErreurEmployeur] = useState(false);
  const [code, setCode] = useState('');
  const [activation, setActivation] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  const charger = useCallback(async () => {
    setPhase('chargement');
    try {
      const situation = await lireSituationEcole();
      setLicence(situation.licence);
      setEmployeur(situation.employeur);
      setPhase('pret');
    } catch {
      // Une panne n'est PAS « pas de licence » : sans ce cas, un étudiant licencié verrait le champ
      // de code et croirait avoir perdu son accès.
      setPhase('erreur');
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const activer = async () => {
    if (activation || caracteresUtiles(code) !== 12) return;
    setActivation(true);
    setRefus(null);
    try {
      const { organisationNom } = await accepterCodeEcole(code);
      setLicence({ organisationNom, logoKey: null, consentement: false });
    } catch (e) {
      const cle = e instanceof CodeEcoleError ? e.code : 'inconnu';
      setRefus(t(`licenceEcole.erreurs.${cle}`));
    } finally {
      setActivation(false);
    }
  };

  const changerConsentementEmployeur = async (donne: boolean) => {
    if (!employeur || employeurEnCours) return;
    const avant = employeur;
    setEmployeurEnCours(true);
    setErreurEmployeur(false);
    setEmployeur({ ...employeur, consentement: donne });
    try {
      await definirConsentementEmployeur(donne);
    } catch {
      // Jamais d'accord affiché que le serveur n'a pas enregistré : l'entreprise ne verrait rien, ou verrait trop.
      setEmployeur(avant);
      setErreurEmployeur(true);
    } finally {
      setEmployeurEnCours(false);
    }
  };

  const voit = t('licenceEcole.voit') as unknown as string[];
  const voitEmployeur = t('licenceEcole.employeurVoit') as unknown as string[];

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScreenHeader onBack={onBack} title={t('licenceEcole.title')} testID="licence-ecole-back" />
      <ScrollView contentContainerStyle={[styles.content, large && styles.contentL]} keyboardShouldPersistTaps="handled">
        {phase === 'chargement' ? (
          <View style={styles.centre}>
            <ActivityIndicator color={colors.bluePrimary} />
            <Text style={styles.muted}>{t('licenceEcole.loading')}</Text>
          </View>
        ) : null}

        {phase === 'erreur' ? (
          <View style={styles.centre}>
            <Text style={styles.erreur}>{t('licenceEcole.loadError')}</Text>
            <Button title={t('licenceEcole.retry')} onPress={() => void charger()} variant="secondary" testID="licence-ecole-retry" />
          </View>
        ) : null}

        {phase === 'pret' ? (
          <>
            {licence ? (
              <View style={styles.carte} testID="licence-ecole-active">
                <Text style={styles.carteTitre}>{t('licenceEcole.active')}</Text>
                <Text style={styles.paragraphe}>{t('licenceEcole.via', { nom: licence.organisationNom })}</Text>
              </View>
            ) : (
              <View style={styles.carte}>
                <Text style={styles.paragraphe}>{t('licenceEcole.intro')}</Text>
                <Text style={styles.label}>{t('licenceEcole.codeLabel')}</Text>
                <TextInput
                  style={styles.champ}
                  value={code}
                  onChangeText={setCode}
                  placeholder={t('licenceEcole.codePlaceholder')}
                  placeholderTextColor={colors.grey400}
                  autoCapitalize="characters"
                  autoCorrect={false}
                  maxLength={20}
                  testID="licence-ecole-code"
                />
                {refus ? <Text style={styles.erreur}>{refus}</Text> : null}
                <Button
                  title={activation ? t('licenceEcole.activating') : t('licenceEcole.activer')}
                  onPress={() => void activer()}
                  loading={activation}
                  disabled={caracteresUtiles(code) !== 12}
                  testID="licence-ecole-activer"
                />
              </View>
            )}

            {/* La transparence passe AVANT l'activation : l'étudiant sait ce que voit son école avant
                de s'engager, pas après. */}
            <View style={styles.carte}>
              <Text style={styles.carteTitre}>{t('licenceEcole.voitTitre')}</Text>
              {voit.map((ligne) => (
                <View key={ligne} style={styles.ligne}>
                  <Text style={styles.puce}>•</Text>
                  <Text style={styles.paragrapheLigne}>{ligne}</Text>
                </View>
              ))}
              <Text style={styles.muted}>{t('licenceEcole.neVoitPas')}</Text>
            </View>

            {employeur ? (
              <>
                <View style={styles.carte} testID="employeur-carte">
                  <Text style={styles.carteTitre}>{t('licenceEcole.employeurTitre')}</Text>
                  <Text style={styles.paragraphe}>
                    {t('licenceEcole.employeurContrat', { nom: employeur.entrepriseNom, date: dateContrat(employeur.debutContrat) })}
                  </Text>
                </View>
                {/* Comme pour l'école : ce que l'entreprise verrait est dit AVANT de demander l'accord. */}
                <View style={styles.carte}>
                  <Text style={styles.carteTitre}>{t('licenceEcole.employeurVoitTitre')}</Text>
                  {voitEmployeur.map((ligne) => (
                    <View key={ligne} style={styles.ligne}>
                      <Text style={styles.puce}>•</Text>
                      <Text style={styles.paragrapheLigne}>{ligne}</Text>
                    </View>
                  ))}
                  <Text style={styles.muted}>{t('licenceEcole.employeurNeVoitPas')}</Text>
                </View>
                <View style={styles.carte}>
                  <View style={styles.ligneConsentement}>
                    <View style={styles.texteConsentement}>
                      <Text style={styles.carteTitre}>{t('licenceEcole.employeurConsentTitre')}</Text>
                      <Text style={styles.muted}>{t('licenceEcole.employeurConsentBody')}</Text>
                    </View>
                    <Switch
                      value={employeur.consentement}
                      onValueChange={(v) => void changerConsentementEmployeur(v)}
                      disabled={employeurEnCours}
                      testID="employeur-consentement"
                    />
                  </View>
                  {erreurEmployeur ? <Text style={styles.erreur}>{t('licenceEcole.employeurConsentError')}</Text> : null}
                </View>
              </>
            ) : null}
          </>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.grey50 },
  content: { padding: screenPadding, gap: spacing.lg },
  // Écran large : colonne de lecture centrée, pas une ligne de texte de 1200 px.
  contentL: { width: '100%', maxWidth: 720, alignSelf: 'center', padding: spacing.xl, paddingBottom: spacing.xxl },
  centre: { alignItems: 'center', gap: spacing.md, paddingVertical: spacing.xxl },
  carte: { backgroundColor: colors.white, borderRadius: radius.lg, padding: spacing.lg, gap: spacing.md },
  carteTitre: { ...typography.h3, color: colors.navy },
  paragraphe: { ...typography.body, color: colors.grey700 },
  paragrapheLigne: { ...typography.body, color: colors.grey700, flex: 1 },
  ligne: { flexDirection: 'row', gap: spacing.sm },
  puce: { ...typography.body, color: colors.grey500 },
  label: { ...typography.caption, color: colors.grey500, textTransform: 'uppercase' },
  champ: {
    ...typography.bodyMd,
    color: colors.night,
    borderWidth: 1,
    borderColor: colors.grey200,
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    letterSpacing: 2,
  },
  muted: { ...typography.small, color: colors.grey500 },
  erreur: { ...typography.small, color: colors.error },
  ligneConsentement: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  texteConsentement: { flex: 1, gap: spacing.xs },
});
